import * as R from 'remeda'
import { zodResolver } from '@hookform/resolvers/zod'
import { Button, Chip, CircularProgress, ListItem, Stack, Typography } from '@mui/material'
import { Controller, useForm } from 'react-hook-form'
import { ControlledAutocomplete } from '~/ui/components/ControlledAutocomplete'
import { countryList } from './constants'
import { routes } from '~/features/routes'
import { capitalize, fromEntries } from 'remeda'
import { useCreateVisitor } from './hooks/useCreateVisitor'
import { useGetEmployees } from './hooks/useGetEmployees'
import { toast } from 'sonner'
import { ControlledTextField } from '~/ui/components/ControlledTextField'
import { useCallback, useMemo } from 'react'
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3'
import { useUpdateInventory } from '~/features/visitors/hooks/useUpdateInventory'
import { useGetInventory } from '~/features/hooks/useGetInventory'
import { InventoryModelsMap, visitorFormSchema, VisitorSchema } from '~/features/visitors/schemas'
import { storeLocations } from '~/utils/constants'
import { useValidateTotal } from '~/features/visitors/hooks/useValidateTotal'
import { FormWrapper } from '~/ui/components/FormWrapper'
import { useGetAgeGroups } from './hooks/useGetAgeGroups'
import { useGetAgencies } from '../agencies/hooks/useGetAgencies'

const WOLLEM_GIFTS_AGENCY_ID = 33

export const VisitorForm = () => {
  const { data: agencies, isLoading: isAgenciesLoading } = useGetAgencies()
  const { data: employees, isLoading: isEmployeesLoading } = useGetEmployees()
  const { data: ageGroups, isLoading: ageGroupsLoading } = useGetAgeGroups()

  const { mutate: createVisitor, isPending: isCreatingVisitor } = useCreateVisitor()

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isValid },
    watch,
  } = useForm<VisitorSchema>({
    defaultValues: {
      created_at: new Date().toISOString(),
      pax: 0,
      agency: '',
      info: '',
      employee: '',
      total: 0,
      country: '',
      // TODO products is deprecated - remove it later
      products: [],
      product_ids: [],
      payment_type: '',
      store: 'wollem',
      age_group: null,
      message_id: null,
      has_refund: false,
    },
    resolver: zodResolver(
      visitorFormSchema.superRefine((data, ctx) => {
        if (!data.age_group) {
          ctx.addIssue({
            code: 'custom',
            path: ['age_group'],
            message: 'Age group is required',
          })
        }
      })
    ),
    mode: 'onChange',
  })

  const { data: inventory, isFetching: isLoadingModels } = useGetInventory({
    filters: [
      { column: 'location', operator: 'eq', value: watch('store') },
      { column: 'status', operator: 'in', value: ['available'] },
    ],
  })

  // map will get rid of duplicates
  const { inventoryMap, productIds } = useMemo<{
    inventoryMap: InventoryModelsMap | null
    productIds: string[]
  }>(() => {
    if (!inventory) return { inventoryMap: null, productIds: [] }
    return {
      inventoryMap: fromEntries(inventory.map((el) => [el.id.toString(), el])),
      productIds: inventory.map((el) => el.id.toString()),
    }
  }, [inventory])

  const { mutateAsync: updateInventory, isPending: isUpdatingInventory } = useUpdateInventory()

  const handleUpdateInventory = useCallback(
    async (idsToUpdate: string[], salesId: number) => {
      if (!idsToUpdate) {
        return
      }

      await updateInventory(
        idsToUpdate.map((id) => ({ id: Number(id), status: 'sold', sales: salesId }))
      )
    },
    [updateInventory]
  )

  const { ValidateTotalDialog, validateTotal } = useValidateTotal()

  const agencyName = watch('agency')

  const isWollemGifts = useMemo(() => {
    const agency = agencies?.find((a) => a.agency_id === WOLLEM_GIFTS_AGENCY_ID)
    return agency && agencyName ? agencyName.includes(agency?.agency_name) : false
  }, [agencies, agencyName])

  const sendNotification = async (data: VisitorSchema, shouldCheckPriceAccuracy = true) => {
    if (!inventoryMap) return

    if (shouldCheckPriceAccuracy && inventoryMap && data.product_ids && data.total !== 0) {
      const isTotalValid = validateTotal({
        onSubmit: (data) => sendNotification(data, false),
        formData: data,
        modelsMapData: inventoryMap,
      })

      if (!isTotalValid) return
    }

    createVisitor(data, {
      onSuccess: async (response) => {
        const productIds = data.product_ids && data.product_ids.length > 0 ? data.product_ids : null
        if (!productIds) return reset()

        if (data.total > 0 || isWollemGifts) {
          await handleUpdateInventory(productIds, response.id)
        }

        reset()
      },
    })
  }

  const isLoading = isCreatingVisitor || isUpdatingInventory

  const products = watch('products')
  const productIdsFormData = watch('product_ids')
  const hasProducts = !!productIdsFormData?.length

  const ageGroupOptions = ageGroups?.map(({ id }) => id) ?? []

  return (
    <Stack direction="row" overflow="hidden" flex={1}>
      {ValidateTotalDialog}
      <FormWrapper backRoute={routes.sales.menu.visitors.route}>
        <Stack
          component="form"
          onSubmit={handleSubmit((data) => sendNotification(data))}
          gap={2}
          sx={{ overflowY: 'auto' }}
          py={2}
        >
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <Controller
              control={control}
              name="created_at"
              render={({ field }) => (
                <LocalizationProvider dateAdapter={AdapterDateFns}>
                  <DatePicker
                    value={new Date(field.value)}
                    onChange={(value) => {
                      field.onChange(value?.toISOString())
                    }}
                    slotProps={{ textField: { size: 'small' } }}
                  />
                </LocalizationProvider>
              )}
            />
            <ControlledAutocomplete
              errors={errors}
              control={control}
              name="store"
              label="Store"
              options={storeLocations}
              getOptionLabel={(value) => capitalize(value)}
              sx={{ flex: 1 }}
              onChange={() => {
                if (hasProducts) {
                  toast.warning('Products are reset!', {
                    duration: 20000,
                    id: 'check-product-field',
                    description:
                      'You changed "Store" while having selected products in place. Make sure the "Products" field has correct values.',
                  })
                  reset({ ...watch(), product_ids: [] })
                }
              }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledAutocomplete
              errors={errors}
              control={control}
              name="employee"
              label="Employee"
              options={employees?.map(({ full_name }) => full_name) ?? []}
              loading={isEmployeesLoading}
              required
              sx={{ width: 0.5 }}
            />

            <ControlledAutocomplete
              control={control}
              name="country"
              errors={errors}
              label="Country"
              options={countryList}
              required
              sx={{ width: 0.5 }}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledAutocomplete
              label="Agency"
              options={
                agencies?.map(({ agency_name, agency_id }) => `${agency_name} - ${agency_id}`) ?? []
              }
              loading={isAgenciesLoading}
              control={control}
              name="agency"
              errors={errors}
              required
              sx={{ width: 0.5 }}
            />
            <ControlledAutocomplete
              label="Age group"
              options={ageGroupOptions}
              getOptionLabel={(id) => {
                const ageGroup = ageGroups?.find((el) => el.id === id)
                return ageGroup?.name ?? ''
              }}
              loading={ageGroupsLoading}
              control={control}
              name="age_group"
              errors={errors}
              required
              sx={{ width: 0.5 }}
            />
          </Stack>

          <ControlledTextField
            control={control}
            name="info"
            label="Info"
            multiline
            size="small"
            errors={errors}
            required
          />

          {products && products.length ? (
            <Stack gap={1}>
              <Stack direction="row" gap={1}>
                {products.map((p) => (
                  <Chip label={p} size="small" key={p} />
                ))}
              </Stack>

              <Typography color="warning" variant="caption">
                This entry is outdated.
              </Typography>
            </Stack>
          ) : (
            <ControlledAutocomplete
              errors={errors}
              label="Products"
              noOptionsText="Type stock code..."
              options={productIds}
              getOptionLabel={(id) => {
                const product = inventoryMap?.[id]
                const label = product
                  ? `${product.models_v2.product_type === 'ring' ? `${product.stock_code_v2} - size ${product.size}` : product.stock_code_v2}`
                  : 'N/A'
                return label
              }}
              renderOption={(props, id) => {
                const product = inventoryMap?.[id]
                return (
                  <ListItem {...props} key={product?.id}>
                    <Stack sx={{ gap: 0.5 }}>
                      <Typography variant="body2">{product?.stock_code_v2}</Typography>
                      <Stack direction="row" sx={{ gap: 1 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Store : {product?.location}
                        </Typography>
                        <Typography variant="subtitle2" color="text.secondary">
                          Size : {product?.size}
                        </Typography>
                      </Stack>
                    </Stack>
                  </ListItem>
                )
              }}
              filterOptions={(options, { inputValue }) => {
                if (inputValue.length < 2) return []
                const query = inputValue.toLowerCase()
                return R.pipe(
                  options,
                  R.filter(
                    (id) => !!inventoryMap?.[id]?.stock_code_v2.toLowerCase().includes(query)
                  ),
                  R.uniqueBy((id) => {
                    const product = inventoryMap?.[id]
                    return product ? `${product.stock_code_v2}-${product.size}` : id
                  })
                )
              }}
              loading={isLoadingModels}
              multiple
              control={control}
              name="product_ids"
              filterSelectedOptions
            />
          )}
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={{ xs: 2, sm: 1 }}>
            <ControlledTextField
              control={control}
              name="total"
              label="Total"
              size="small"
              errors={errors}
              fullWidth
              disabled={!hasProducts}
              helperText="Add products to enable"
            />
            <ControlledTextField
              control={control}
              name="pax"
              label="Pax"
              size="small"
              errors={errors}
              required
            />
          </Stack>

          <Button
            type="submit"
            variant="contained"
            disabled={!isValid}
            endIcon={isLoading ? <CircularProgress size={14} /> : null}
          >
            Create Visitor
          </Button>
        </Stack>
      </FormWrapper>
    </Stack>
  )
}
